import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { PlatformShell } from '../shell/PlatformShell';
import { DMWK_MENU, type MenuItem, type SubItem } from '../shell/platform-menu';
import { SettingsPage } from '../settings/SettingsPage';
import { TasksPage } from '../tasks/TasksPage';
import { HelpdeskPage } from '../helpdesk/HelpdeskPage';
import { KlientiPage } from '../klienti/KlientiPage';
import { VyuctovaniKlientiPage, type VyuctovaniKlientiConfig } from '../settlement/VyuctovaniKlientiPage';
import { useScrollRestore } from '../shell/useScrollRestore';
import { SpaceDashboard } from '../shell/SpaceDashboard';
import '../crm/crm-redesign.css';

type Route = { moduleId: string; childId?: string; detail?: string };

const VYUCTOVANI: VyuctovaniKlientiConfig = { space: 'dmwk', eyebrow: 'DMWK', manageKey: 'vyuctovani:manage', currencies: ['CZK'] };
const KLIENTI: Route = { moduleId: 'klienti', childId: 'dmwk-klienti-seznam' };
const FINANCE: Route = { moduleId: 'finance', childId: 'dmwk-vyuctovani-klienti' };

function routeToPath(route: Route): string {
  if (route.moduleId === 'settings') return '/nastaveni';
  if (route.moduleId === 'tasks') return '/ukoly';
  if (route.moduleId === 'helpdesk') return '/helpdesk';
  if (route.childId) {
    const mod = DMWK_MENU.find((m) => m.id === route.moduleId);
    const child = mod?.children?.find((c) => c.id === route.childId);
    if (child?.path) return route.detail ? `${child.path}/${route.detail}` : child.path;
  }
  return '/';
}

function pathToRoute(pathname: string): Route {
  if (pathname === '/nastaveni') return { moduleId: 'settings' };
  if (pathname === '/ukoly') return { moduleId: 'tasks' };
  if (pathname === '/helpdesk') return { moduleId: 'helpdesk' };
  for (const mod of DMWK_MENU) {
    for (const child of mod.children ?? []) {
      if (pathname === child.path) return { moduleId: mod.id, childId: child.id };
      if (pathname.startsWith(child.path + '/')) {
        const detail = decodeURIComponent(pathname.slice(child.path.length + 1).split('/')[0] ?? '');
        return { moduleId: mod.id, childId: child.id, detail: detail || undefined };
      }
    }
  }
  return { moduleId: 'dashboard' };
}

export function DmwkShell() {
  const { me } = useAuth();
  const [route, setRoute] = useState<Route>(() => pathToRoute(window.location.pathname));
  const readTaskId = (): number | null => {
    const raw = new URLSearchParams(window.location.search).get('taskId');
    return raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : null;
  };
  const [taskDetailId, setTaskDetailId] = useState<number | null>(readTaskId);

  useEffect(() => {
    const base = routeToPath(route);
    if (route.moduleId === 'tasks') {
      const target = base + (taskDetailId != null ? `?taskId=${taskDetailId}` : '');
      if (target !== window.location.pathname + window.location.search) window.history.pushState(null, '', target);
      return;
    }
    if (base !== window.location.pathname) window.history.pushState(null, '', base);
  }, [route, taskDetailId]);

  useEffect(() => {
    const onPop = () => { setRoute(pathToRoute(window.location.pathname)); setTaskDetailId(readTaskId()); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useScrollRestore(`${routeToPath(route)}|${taskDetailId ?? ''}`);

  if (!me) return null;

  const selectModule = (item: MenuItem) => {
    const first = item.children?.[0];
    setRoute(first ? { moduleId: item.id, childId: first.id } : { moduleId: item.id });
  };
  const selectChild = (item: MenuItem, child: SubItem) => {
    setRoute({ moduleId: item.id, childId: child.id });
  };
  const openClientDeals = (partnerId: number) => {
    const next: Route = { ...KLIENTI, detail: String(partnerId) };
    window.history.pushState(null, '', `${routeToPath(next)}?tab=dealy`);
    setRoute(next);
  };

  const module = DMWK_MENU.find((m) => m.id === route.moduleId) ?? null;
  const child = module?.children?.find((c) => c.id === route.childId) ?? null;
  const isTasksActive = route.moduleId === 'tasks';
  const isHelpdeskActive = route.moduleId === 'helpdesk';
  const isChrome = route.moduleId === 'settings' || isTasksActive || isHelpdeskActive;
  const pageTitle =
    route.moduleId === 'dashboard'
      ? 'Dashboard'
      : module && child
        ? `${module.label} - ${child.label}`
        : (module?.label ?? '');

  return (
    <PlatformShell
      arcRefresh
      menu={DMWK_MENU}
      currentSpaceSlug="dmwk"
      activeModuleId={route.moduleId}
      activeChildId={route.childId}
      isTasksActive={isTasksActive}
      isHelpdeskActive={isHelpdeskActive}
      showTasks
      showHelpdesk
      showCrony={false}
      onSelectModule={selectModule}
      onSelectChild={selectChild}
      onOpenTasks={() => { setTaskDetailId(null); setRoute({ moduleId: 'tasks' }); }}
      onOpenTask={(taskId) => { setTaskDetailId(taskId); setRoute({ moduleId: 'tasks' }); }}
      onOpenHelpdesk={() => setRoute({ moduleId: 'helpdesk' })}
      onOpenSettings={() => setRoute({ moduleId: 'settings' })}
      pageTitle={isChrome ? undefined : pageTitle}
    >
      {route.moduleId === 'settings' ? (
        <SettingsPage />
      ) : isTasksActive ? (
        <TasksPage selectedTaskId={taskDetailId} onSelectTask={setTaskDetailId} />
      ) : isHelpdeskActive ? (
        <HelpdeskPage lockedSpaceId={me.activeSpaceId ?? undefined} />
      ) : route.childId === 'dmwk-klienti-seznam' ? (
        <KlientiPage
          space="dmwk"
          detailId={route.detail}
          onOpenDetail={(id) => setRoute((r) => ({ ...r, detail: id }))}
          onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
        />
      ) : route.childId === 'dmwk-vyuctovani-klienti' ? (
        <VyuctovaniKlientiPage
          config={VYUCTOVANI}
          detailId={route.detail}
          onOpenDetail={(id) => setRoute((r) => ({ ...r, detail: id }))}
          onCloseDetail={() => setRoute((r) => ({ moduleId: r.moduleId, childId: r.childId }))}
          onOpenClient={(_name, partnerId) => openClientDeals(partnerId)}
        />
      ) : (
        <DmwkDashboard onOpenKlienti={() => setRoute(KLIENTI)} onOpenVyuctovani={() => setRoute(FINANCE)} />
      )}
    </PlatformShell>
  );
}

function DmwkDashboard({ onOpenKlienti, onOpenVyuctovani }: { onOpenKlienti: () => void; onOpenVyuctovani: () => void }) {
  return (
    <SpaceDashboard
      eyebrow="DMWK"
      description="Klienti a měsíční fakturace na jednom místě"
      tiles={[
        { title: 'Vyúčtování klientů', description: 'Měsíční fakturace fixních služeb', action: { label: 'Otevřít vyúčtování', onClick: onOpenVyuctovani } },
        { title: 'Seznam klientů', description: 'Fakturační údaje, kontakty a dealy', action: { label: 'Otevřít klienty', onClick: onOpenKlienti } },
      ]}
    />
  );
}
